import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import MobileOnly from './pages/MobileOnly';
import Farms from './pages/Farms';
import FarmDetails from './pages/FarmDetails';
import CreateFarm from './pages/CreateFarm';
import Animals from './pages/Animals';
import AnimalDetails from './pages/AnimalDetails';
import CreateAnimal from './pages/CreateAnimal';
import Profile from './pages/Profile';
import AdminLayout from './components/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminRegions from './pages/admin/AdminRegions';
import AdminAddAnimal from './pages/admin/AdminAddAnimal';
import AdminAnimals from './pages/admin/AdminAnimals';
import AdminAnimalDetails from './pages/admin/AdminAnimalDetails';
import AdminUsers from './pages/admin/AdminUsers';
import AdminInseminators from './pages/admin/AdminInseminators';
import AdminDnaLabs from './pages/admin/AdminDnaLabs';
import AdminAuction from './pages/admin/AdminAuction';
import AdminSalesPlans from './pages/admin/AdminSalesPlans';
import AdminFeedingAreas from './pages/admin/AdminFeedingAreas';
import { useAuthStore, type UserRole } from './store/auth.store';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

const ROLE_HIERARCHY: Record<UserRole, number> = {
  User: 0,
  Seller: 1,
  Moderator: 2,
  Admin: 3,
};

function RoleRoute({ children, minRole }: { children: React.ReactNode; minRole: UserRole }) {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user || ROLE_HIERARCHY[user.role] < ROLE_HIERARCHY[minRole]) {
    return <Navigate to="/mobile-only" replace />;
  }
  return <>{children}</>;
}

function App() {
  const { checkAuth, isLoading } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Не запускаем проверки маршрутов до восстановления сохранённой сессии.
  if (isLoading) {
    return <div role="status" aria-live="polite">Загрузка…</div>;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route
            path="mobile-only"
            element={
              <ProtectedRoute>
                <MobileOnly />
              </ProtectedRoute>
            }
          />
          <Route
            path="farms"
            element={
              <RoleRoute minRole="Moderator">
                <Farms />
              </RoleRoute>
            }
          />
          <Route
            path="farms/create"
            element={
              <RoleRoute minRole="Moderator">
                <CreateFarm />
              </RoleRoute>
            }
          />
          <Route
            path="farms/:id"
            element={
              <RoleRoute minRole="Moderator">
                <FarmDetails />
              </RoleRoute>
            }
          />
          <Route
            path="animals"
            element={
              <RoleRoute minRole="Moderator">
                <Animals />
              </RoleRoute>
            }
          />
          <Route
            path="animals/:id"
            element={
              <RoleRoute minRole="Moderator">
                <AnimalDetails />
              </RoleRoute>
            }
          />
          <Route
            path="animals/create"
            element={
              <RoleRoute minRole="Moderator">
                <CreateAnimal />
              </RoleRoute>
            }
          />
          <Route
            path="profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin"
            element={
              <RoleRoute minRole="Admin">
                <AdminLayout />
              </RoleRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="regions" element={<AdminRegions />} />
            <Route path="animals" element={<AdminAnimals />} />
            <Route path="animals/add" element={<AdminAddAnimal />} />
            <Route path="animals/:id" element={<AdminAnimalDetails />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="inseminators" element={<AdminInseminators />} />
            <Route path="dna-labs" element={<AdminDnaLabs />} />
            <Route path="auction" element={<AdminAuction />} />
            <Route path="sales-plans" element={<AdminSalesPlans />} />
            <Route path="feeding-areas" element={<AdminFeedingAreas />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
